import { supabase } from '@/integrations/supabase/client';
import { gameNotificationService } from './gameNotificationService';
import { gameManagementService } from './gameManagementService';
import { GameStateService } from './gameStateService';
import { venueGamesService } from './venueGamesService';
import { toast } from 'sonner';

export interface QueueActionResult {
  success: boolean;
  message: string;
  redirect?: string;
}

export const queueActionService = {
  // Handle game approval actions
  async handleGameApproval(gameId: string, action: 'approve' | 'reject', userId: string, userType: 'supabase' | 'internal' = 'supabase', reason?: string): Promise<QueueActionResult> {
    try {
      console.log('🎮 QUEUE ACTION: Game approval:', { gameId, action, userId, userType });

      // Check if this is a venue game
      const { data: venueGame } = await supabase
        .from('venue_games')
        .select('*')
        .eq('id', gameId)
        .maybeSingle();

      if (venueGame) {
        // Handle venue game approval
        const success = await venueGamesService.updateGameStatus(gameId, action === 'approve' ? 'approved' : 'rejected', userId, reason);
        
        if (success) {
          return {
            success: true,
            message: `Game ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
            redirect: '/venue-management'
          };
        } else {
          return {
            success: false,
            message: `Failed to ${action} game`
          };
        }
      }

      // Handle regular game approval using state service
      const newState = action === 'approve' ? 'approved' : 'rejected';
      const success = await GameStateService.transitionGameState(
        gameId,
        newState,
        userId,
        'venue_manager',
        reason
      );

      if (success) {
        return {
          success: true,
          message: `Game ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
          redirect: `/games/${gameId}`
        };
      } else {
        return {
          success: false,
          message: `Failed to ${action} game`
        };
      }
    } catch (error) {
      console.error('Error handling game approval:', error);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
  },

  // Handle scorekeeper applications
  async handleScorekeeperApplication(gameId: string, action: 'apply' | 'approve' | 'reject', userId: string, userType: 'supabase' | 'internal' = 'supabase', applicationId?: string): Promise<QueueActionResult> {
    try {
      console.log('🎮 QUEUE ACTION: Scorekeeper application:', { gameId, action, userId, userType });

      if (action === 'apply') {
        const success = await GameStateService.applyForGame(gameId, userId);
        return {
          success,
          message: success ? 'Application submitted successfully' : 'Failed to submit application',
          redirect: success ? `/games/${gameId}` : undefined
        };
      }

      if (action === 'approve' && applicationId) {
        const success = await GameStateService.approveScorekeeper(applicationId, userId);
        return {
          success,
          message: success ? 'Scorekeeper approved successfully' : 'Failed to approve scorekeeper',
          redirect: success ? `/games/${gameId}` : undefined
        };
      }

      if (action === 'reject' && applicationId) {
        const { error } = await supabase
          .from('scorekeeper_applications')
          .update({
            status: 'rejected',
            rejected_by: userId,
            rejected_at: new Date().toISOString()
          })
          .eq('id', applicationId);

        if (error) throw error;

        return {
          success: true,
          message: 'Application rejected',
          redirect: `/games/${gameId}`
        };
      }

      return {
        success: false,
        message: 'Invalid action or missing data'
      };
    } catch (error) {
      console.error('Error handling scorekeeper application:', error);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
  },

  // Handle team confirmations
  async handleTeamConfirmation(gameId: string, userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<QueueActionResult> {
    try {
      console.log('🎮 QUEUE ACTION: Team confirmation:', { gameId, userId, userType });

      // Check if this is a venue game first
      const { data: venueGame } = await supabase
        .from('venue_games')
        .select('*')
        .eq('id', gameId)
        .maybeSingle();

      if (venueGame) {
        // Use game notification service for venue games
        await gameNotificationService.acceptGameParticipation(gameId, userId, userType);
        return {
          success: true,
          message: 'Team participation confirmed',
          redirect: `/games/${gameId}`
        };
      }

      // Handle regular game confirmation
      // Find which team the user manages
      const { data: game } = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name, created_by),
          away_team:teams!games_away_team_id_fkey(team_id, team_name, created_by)
        `)
        .eq('id', gameId)
        .single();

      if (!game) {
        return { success: false, message: 'Game not found' };
      }

      let teamSide: 'home' | 'away' | null = null;
      let teamId: number | null = null;

      // Check if user manages home team
      if (game.home_team?.created_by === userId) {
        teamSide = 'home';
        teamId = game.home_team_id;
      }
      // Check if user manages away team
      else if (game.away_team?.created_by === userId) {
        teamSide = 'away';
        teamId = game.away_team_id;
      }
      // Check if user is a coach
      else {
        const { data: coachTeam } = await supabase
          .from('team_coaches')
          .select('team_id')
          .eq('coach_id', userId)
          .in('team_id', [game.home_team_id, game.away_team_id].filter(Boolean))
          .maybeSingle();

        if (coachTeam) {
          teamSide = coachTeam.team_id === game.home_team_id ? 'home' : 'away';
          teamId = coachTeam.team_id;
        }
      }

      if (!teamSide || !teamId) {
        return { success: false, message: 'You are not authorized to confirm this team' };
      }

      await gameManagementService.confirmGame(gameId, teamSide, teamId, userId, userType);
      
      return {
        success: true,
        message: 'Team confirmed successfully',
        redirect: `/games/${gameId}`
      };
    } catch (error) {
      console.error('Error handling team confirmation:', error);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
  },

  // Handle player status updates
  async handlePlayerStatusUpdate(gameId: string, participantId: string, status: 'confirmed' | 'declined' | 'ready_to_play', userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<QueueActionResult> {
    try {
      console.log('🎮 QUEUE ACTION: Player status update:', { gameId, participantId, status, userId, userType });

      const { error } = await supabase
        .from('game_participants')
        .update({
          participation_status: status,
          status_updated_at: new Date().toISOString()
        })
        .eq('id', participantId)
        .eq('player_id', userId);

      if (error) throw error;

      return {
        success: true,
        message: `Status updated to ${status}`,
        redirect: `/games/${gameId}`
      };
    } catch (error) {
      console.error('Error updating player status:', error);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
  },

  // Handle score submissions
  async handleScoreSubmission(gameId: string, homeScore: number, awayScore: number, userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<QueueActionResult> {
    try {
      console.log('🎮 QUEUE ACTION: Score submission:', { gameId, homeScore, awayScore, userId, userType });

      const winningTeam = homeScore > awayScore ? 'home' : awayScore > homeScore ? 'away' : 'draw';

      await gameManagementService.recordGameResult(gameId, {
        home_team_score: homeScore,
        away_team_score: awayScore,
        winning_team: winningTeam,
        recorded_by: userId,
        recorded_by_type: userType,
        final_status: 'completed'
      });

      // Update game status to completed
      await GameStateService.transitionGameState(
        gameId,
        'completed',
        userId,
        'scorekeeper'
      );

      return {
        success: true,
        message: 'Score submitted successfully',
        redirect: `/games/${gameId}`
      };
    } catch (error) {
      console.error('Error submitting score:', error);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
  },

  // Execute queue item action
  async executeQueueAction(queueItem: any, userId: string, userType: 'supabase' | 'internal' = 'supabase'): Promise<QueueActionResult> {
    console.log('🎮 QUEUE ACTION: Executing action for:', queueItem.type, queueItem.id);

    switch (queueItem.type) {
      case 'game_approval':
        // For queue items, we might need additional context for approve/reject
        // This would typically be handled by the UI component
        return await this.handleGameApproval(queueItem.game_id, 'approve', userId, userType);

      case 'scorekeeper_application':
        if (queueItem.application_id) {
          return await this.handleScorekeeperApplication(queueItem.game_id, 'approve', userId, userType, queueItem.application_id);
        } else {
          return await this.handleScorekeeperApplication(queueItem.game_id, 'apply', userId, userType);
        }

      case 'team_confirmation':
        return await this.handleTeamConfirmation(queueItem.game_id, userId, userType);

      case 'player_status':
        // Would need participant ID from queue item
        if (queueItem.participant_id) {
          return await this.handlePlayerStatusUpdate(queueItem.game_id, queueItem.participant_id, 'confirmed', userId, userType);
        }
        return { success: false, message: 'Missing participant information' };

      case 'score_submission':
        // This would typically be handled by a form, not directly from queue
        return { success: false, message: 'Score submission requires a form interface' };

      default:
        return { success: false, message: 'Unknown action type' };
    }
  },

  // Mark queue item as completed
  async markQueueItemCompleted(queueItemId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('queue_items')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', queueItemId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking queue item as completed:', error);
    }
  }
};
